import { routePaths } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useTakePicture } from "@/hooks/useTakePicture";
import { OrganizationSettings } from "@/libs/api/types";
import { useSettings } from "@/libs/settings/hooks/useSettings";
import { useTourStop } from "@/libs/tours-detail/hooks/useTourStop";
import { formatPrice } from "@/libs/tours-detail/utils/shared";
import { cn } from "@/utils/cn";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  Banknote,
  Check,
  CheckIcon,
  ChevronLeft,
  CreditCard,
  FileText,
  HandCoins,
  NotebookPen,
  PhoneCallIcon,
  PhoneIcon,
  UserIcon,
  XCircle,
} from "lucide-react";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";

/**
 * TODO: This file contains AI generated Code. The code in this file may not be complete.
 * We defenetly need to refactor this.
 */

const QUICK_AMOUNTS = [10, 20, 50];

export function DeliveryDetailPage() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { id, stopId, taskId } = useParams<{
    id: string;
    stopId: string;
    taskId: string;
  }>();

  const isBulkTask = taskId?.startsWith("bulk");

  const { currentStop, deliverTourStop, uploadDeliveryProofImage } =
    useTourStop(stopId as string);

  const { toast } = useToast();

  const { getSettingValue } = useSettings();

  const currentTask = currentStop?.items.find(
    (item) => item.id === (taskId as string),
  );

  const [step, setStep] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<string>("cash");
  const [cashAmount, setCashAmount] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [isDeliveryStarted, setIsDeliveryStarted] = useState(false);
  const signatureRef = useRef<SignatureCanvas | null>(null);

  const calculateChange = () => {
    const given = parseFloat(cashAmount);
    if (isNaN(given)) return 0;
    const due = currentTask?.price ? currentTask.price / 100 : 0;
    return given - due;
  };

  const handlePaymentMethodChange = (value: string) => {
    setPaymentMethod(value);
    setCashAmount("");

    if (value === "card" && currentTask?.price) {
      setCashAmount((currentTask.price / 100).toString());
    }
  };

  const handleCashAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCashAmount(e.target.value);
  };

  const handleQuickAmount = (amount: number) => {
    setCashAmount(amount.toString());
  };

  const handleCashConfirm = () => {
    setStep(1);
  };

  const handleSignatureEnd = () => {
    if (signatureRef.current) {
      setSignature(signatureRef.current.toDataURL());
    }
  };

  const { takePicture, photo } = useTakePicture();

  const handleDeliveryProofUpload = async () => {
    await takePicture();
  };

  const handleAlternativeDelivery = () => {
    if (!currentTask) return;

    navigate(
      routePaths.createAlternativeDeliveryPath(
        id as string,
        stopId as string,
        currentTask.id,
      ),
    );
  };

  const handleAlternativeStorageDelivery = () => {
    if (!currentTask) return;

    navigate(
      routePaths.createAlternativeStorageDeliveryPath(
        id as string,
        stopId as string,
        currentTask.id,
      ),
    );
  };

  const handleClearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setSignature(null);
    }
  };

  const handleNotDelivered = () => {
    if (currentTask?.id && currentTask?.parentId) {
      navigate(
        routePaths.createDeliveryFailedPath(
          id as string,
          stopId as string,
          currentTask.id,
        ),
      );
    }
  };

  const renderTimeline = () => {
    const steps = [
      t("orderDelivery.timeline.payment"),
      t("orderDelivery.timeline.signature"),
      t("orderDelivery.timeline.note"),
    ];

    return (
      <div className="flex items-center relative p-4">
        {steps.map((stepName, index) => (
          <React.Fragment key={stepName}>
            <div className="flex flex-col items-center relative z-10">
              <div
                className={cn(
                  `w-8 h-8 rounded-full flex items-center justify-center bg-[#E3E3E3] text-white`,
                  {
                    ["bg-success text-primary-foreground"]: step > index,
                    ["bg-primary text-primary-foreground"]: step === index,
                  },
                )}
              >
                {step > index ? <Check className="w-5 h-5" /> : index + 1}
              </div>
              <span
                className={cn("text-sm mt-1 text-black", {
                  "text-primary": step === index,
                })}
              >
                {stepName}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className="flex-grow mx-2">
                <div className="h-0.5 bg-[#383838]"></div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderPaymentStep = () => (
    <div className="space-y-4">
      <RadioGroup
        value={paymentMethod || ""}
        onValueChange={handlePaymentMethodChange}
        className="grid grid-cols-2 gap-4 p-4"
      >
        {[
          {
            value: "card",
            label: t("orderDelivery.payment.methods.card"),
            icon: CreditCard,
          },
          {
            value: "cash",
            label: t("orderDelivery.payment.methods.cash"),
            icon: Banknote,
          },
          {
            value: "exemptPayment",
            label: t("orderDelivery.payment.methods.exemptPayment"),
            icon: FileText,
          },
          {
            value: "noPayment",
            label: t("orderDelivery.payment.methods.noPayment"),
            icon: XCircle,
          },
        ].map(({ value, label, icon: Icon }) => (
          <div key={value}>
            <RadioGroupItem value={value} id={value} className="peer sr-only" />
            <Label
              htmlFor={value}
              className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-secondary [&:has([data-state=checked])]:border-primary"
            >
              <Icon className="mb-3 h-6 w-6" />
              {label}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );

  const renderSignatureStep = () => (
    <div className="space-y-4 p-4">
      <div className="border border-gray-300 rounded-md">
        <SignatureCanvas
          ref={signatureRef}
          canvasProps={{
            height: 500,
            className: "bg-white rounded-md w-full",
          }}
          onEnd={handleSignatureEnd}
        />
      </div>
    </div>
  );

  const renderNoteStep = () => (
    <div className="space-y-4 p-4">
      <Textarea
        value={note}
        rows={8}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t("orderDelivery.notePlaceholder")}
      />
      {photo?.base64String && (
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="photo">Foto</Label>
          <div className="relative border-2 border-black rounded-md w-full">
            <img
              className="w-full aspect-auto object-cover rounded-sm"
              src={`data:image/png;base64,${photo.base64String}`}
              alt="Beweisfoto"
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderStep = () => {
    if (currentTask?.price === 0 && step === 0) {
      setStep(1);
      setPaymentMethod("");
    }

    switch (step) {
      case 0:
        return renderPaymentStep();
      case 1:
        return renderSignatureStep();
      case 2:
        return renderNoteStep();
      default:
        return null;
    }
  };

  const handleBackToDecision = () => {
    setIsDeliveryStarted(false);
  };

  const handleDelivery = () => {
    if (currentTask?.id && currentTask?.parentId) {
      deliverTourStop(
        currentTask.id,
        currentTask.parentId,
        {
          signature: signature || "",
          driverNote: note,
          paymentCollected: currentTask.price || 0,
          paymentType: paymentMethod,
        },
        isBulkTask,
      );

      if (photo && photo.base64String) {
        uploadDeliveryProofImage(
          photo.base64String,
          currentTask.parentId,
          currentTask.id,
        );
      }

      navigate(
        routePaths.createTourStopPath(
          id as string,
          stopId as string,
          currentTask.type,
        ),
      );
    }
  };

  const handleSignatureNext = () => {
    if (
      getSettingValue(OrganizationSettings.FORCE_SIGNATURE_FOR_DELIVERY) &&
      !signature
    ) {
      toast({
        title: "Unterschrift erforderlich",
        description: "Lass den Kunden unterschreiben, um fortzufahren.",
        variant: "error",
      });
      return;
    }

    setStep(2);
  };

  if (!currentStop || !currentTask) {
    return <Navigate to={routePaths.createTourPath(id!)} />;
  }
  return (
    <div className="h-screen">
      <div className="h-full flex flex-col">
        <div className="bg-white px-4 py-6 border-b-2 border-neutral pt-safe">
          {isDeliveryStarted ? (
            <Link
              to=""
              className="mb-8 flex items-center font-medium gap-1.5 bg-slate-100 w-fit p-2 pr-4 rounded"
              onClick={handleBackToDecision}
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
              {t("common.back")}
            </Link>
          ) : (
            <Link
              to={routePaths.createTourStopPath(
                id as string,
                stopId as string,
                currentTask.type,
              )}
              className="mb-8 flex items-center font-medium gap-1.5 bg-slate-100 w-fit p-2 pr-4 rounded"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
              {t("common.back")}
            </Link>
          )}

          <div className="space-y-2">
            <h3 className="text-xl font-semibold">
              {currentTask.bulkInfo
                ? "Sammelauftrag zustellen"
                : t("orderDelivery.header.title")}
            </h3>

            <div className="space-y-4 mt-6">
              <div className="flex items-center text-sm font-medium text-[#6A80A8]">
                <UserIcon className="w-5 h-5 mr-2" strokeWidth={2.5} />
                {currentTask?.name}
              </div>

              {currentTask?.phoneNumber && (
                <a
                  href={`tel:${currentTask?.phoneNumber}`}
                  className="flex items-center text-sm font-medium text-[#6A80A8]"
                >
                  <PhoneCallIcon className="w-5 h-5 mr-2" strokeWidth={2.5} />
                  {currentTask?.phoneNumber}
                </a>
              )}

              {currentTask?.mobileNumber && (
                <a
                  href={`tel:${currentTask?.mobileNumber}`}
                  className="flex items-center text-sm font-medium text-[#6A80A8]"
                >
                  <PhoneIcon className="w-5 h-5 mr-2" strokeWidth={2.5} />
                  {currentTask?.mobileNumber}
                </a>
              )}

              {currentTask?.note && (
                <div className="flex items-center text-sm font-medium text-[#6A80A8]">
                  <NotebookPen className="w-5 h-5 mr-2" strokeWidth={2.5} />
                  {currentTask?.note}
                </div>
              )}

              {currentTask?.price !== 0 && (
                <div className="flex items-center text-sm font-medium text-[#6A80A8]">
                  <HandCoins className="w-5 h-5 mr-2" strokeWidth={2.5} />
                  {formatPrice(currentTask.price)} €
                </div>
              )}

              {currentTask?.jobNr && (
                <div className="flex gap-2 items-center text-sm font-medium text-[#6A80A8]">
                  <span>Job-Nr:</span>
                  {currentTask?.jobNr}
                </div>
              )}
            </div>

            {currentTask.state === "done" && (
              <div className="bg-green-300 p-3 mt-4 rounded flex text-sm items-center gap-3 font-medium">
                <CheckIcon className="w-4 h-4" strokeWidth={3} />
                Dieser Auftrag wurde schon bearbeitet
              </div>
            )}

            {currentTask.state === "canceled" && (
              <div className="bg-purple-300 p-3 mt-8 rounded flex text-sm items-start gap-3 font-medium">
                <IconInfoCircle className="w-6 h-6" strokeWidth={2} />
                {t("orderDelivery.orderAlreadyCanceled")}
              </div>
            )}
          </div>
        </div>

        <div className="overflow-y-scroll pb-12 bg-list-background h-full">
          {isDeliveryStarted && renderTimeline()}
          {isDeliveryStarted && renderStep()}
        </div>

        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t-2 border-neutral pb-safe">
          {isDeliveryStarted && step === 0 && (
            <div className="flex items-center gap-4">
              {paymentMethod === "cash" && (
                <Drawer>
                  <DrawerTrigger className="flex-1 w-full" asChild>
                    <Button variant="secondary" className="w-full">
                      {t("orderDelivery.payment.calculator.open")}
                    </Button>
                  </DrawerTrigger>
                  <DrawerContent>
                    <DrawerHeader className="pb-12">
                      <DrawerTitle>
                        {t("orderDelivery.payment.calculator.title")}
                      </DrawerTitle>
                      <DrawerDescription>
                        {t("orderDelivery.payment.calculator.description")}
                      </DrawerDescription>
                    </DrawerHeader>

                    <div className="space-y-2 p-4">
                      {cashAmount &&
                        parseFloat(cashAmount) < currentTask?.price / 100 && (
                          <div className="text-sm text-red-500">
                            {t(
                              "orderDelivery.payment.calculator.insufficientAmount",
                            )}
                          </div>
                        )}

                      <Input
                        id="cash-amount"
                        type="number"
                        value={cashAmount}
                        onChange={handleCashAmountChange}
                        placeholder={t(
                          "orderDelivery.payment.calculator.inputLabel",
                        )}
                      />
                      <div className="flex space-x-2 justify-between">
                        {QUICK_AMOUNTS.map((amount) => (
                          <Button
                            key={amount}
                            onClick={() => handleQuickAmount(amount)}
                            variant="outline"
                            className="flex-1"
                          >
                            {amount}€
                          </Button>
                        ))}
                      </div>
                      {cashAmount && (
                        <div className="text-sm">
                          {t("orderDelivery.payment.calculator.changeLabel")}:{" "}
                          {calculateChange().toFixed(2)}€
                        </div>
                      )}
                    </div>
                  </DrawerContent>
                </Drawer>
              )}

              <Button onClick={handleCashConfirm} className="flex-1">
                {t("common.next")}
              </Button>
            </div>
          )}
          {isDeliveryStarted && step === 1 && (
            <div className="flex justify-between items-center gap-2">
              <Button
                onClick={handleClearSignature}
                className="flex-1"
                variant="secondary"
              >
                {t("common.delete")}
              </Button>
              <Button onClick={handleSignatureNext} className="flex-1">
                {t("common.next")}
              </Button>
            </div>
          )}
          {isDeliveryStarted && step === 2 && (
            <div className="flex justify-between items-center gap-2">
              <Button
                variant="secondary"
                className="w-full"
                onClick={handleDeliveryProofUpload}
              >
                {photo
                  ? t("orderDelivery.changeImage")
                  : t("orderDelivery.uploadImage")}
              </Button>
              <Button onClick={handleDelivery} className="w-full">
                {t("common.done")}
              </Button>
            </div>
          )}
          {!isDeliveryStarted && (
            <div className="space-y-4">
              <Button
                className="w-full bg-green-400 hover:bg-green-500 text-black font-semibold"
                onClick={() => setIsDeliveryStarted(true)}
              >
                {t("orderDelivery.actions.metCustomer")}
              </Button>
              <Drawer>
                <DrawerTrigger className="w-full" asChild>
                  <Button className="w-full bg-amber-300 hover:bg-amber-500 text-black font-semibold">
                    {t("orderDelivery.actions.alternativeDelivery")}
                  </Button>
                </DrawerTrigger>
                <DrawerContent>
                  <DrawerHeader>
                    <DrawerTitle>
                      {t("orderDelivery.alternativeDelivery.title")}
                    </DrawerTitle>
                    <DrawerDescription>
                      {t("orderDelivery.alternativeDelivery.description")}
                    </DrawerDescription>
                  </DrawerHeader>

                  <div className="flex flex-col gap-4 p-4">
                    <Button
                      variant="secondary"
                      onClick={handleAlternativeDelivery}
                    >
                      {t("orderDelivery.actions.neighbor")}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleAlternativeStorageDelivery}
                    >
                      {t("orderDelivery.actions.storage")}
                    </Button>
                  </div>
                </DrawerContent>
              </Drawer>
              <Button
                className="w-full bg-red-400 hover:bg-red-500 text-black font-semibold"
                onClick={handleNotDelivered}
              >
                {t("orderDelivery.actions.notDeliverable")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
